import { strapi } from '@strapi/client'
import type { API, FileResponse, FileListResponse, FileQueryParams } from '@strapi/client'
import { useAuthStore } from '@/stores/authStore'
import type { 
  StrapiContentTypeMapping, 
  StrapiSingleContentTypeMapping,
  StrapiContentTypeCreateMapping
} from '@/types/strapiContentTypes'

// File update data type (not exported by @strapi/client)
interface FileUpdateData {
  name?: string
  alternativeText?: string
  caption?: string
}

const baseURL = import.meta.env.VITE_STRAPI_URL || 'http://localhost:1337'

function getStrapiClient() {
  const authStore = useAuthStore()
  return strapi({
    baseURL: `${baseURL}/api`,
    auth: authStore.getToken || undefined,
  })
}



// Type-safe strapiService with method overloads
interface StrapiService {
  // Method overloads for known content types
  find<K extends keyof StrapiContentTypeMapping>(
    contentType: K,
    params?: API.BaseQueryParams
  ): Promise<StrapiContentTypeMapping[K]>
  
  // Generic fallback for unknown content types using proper API response structure
  find<T extends API.Document = API.Document>(
    contentType: string,
    params?: API.BaseQueryParams
  ): Promise<API.DocumentResponseCollection<T>>

  // Method overloads for findOne with known content types
  findOne<K extends keyof StrapiSingleContentTypeMapping>(
    contentType: K,
    documentId: string,
    params?: API.BaseQueryParams
  ): Promise<StrapiSingleContentTypeMapping[K]>
  
  // Generic fallback for unknown content types using proper API response structure
  findOne<T extends API.Document = API.Document>(
    contentType: string,
    documentId: string,
    params?: API.BaseQueryParams
  ): Promise<API.DocumentResponse<T>>

  // Method overloads for create with known content types
  create<K extends keyof StrapiContentTypeCreateMapping>(
    contentType: K,
    data: StrapiContentTypeCreateMapping[K],
    params?: API.BaseQueryParams
  ): Promise<StrapiSingleContentTypeMapping[K]>
  
  // Generic fallback for create operations
  create<T extends API.Document = API.Document>(
    contentType: string,
    data: Partial<T>,
    params?: API.BaseQueryParams
  ): Promise<API.DocumentResponse<T>>

  // Method overloads for update with known content types
  update<K extends keyof StrapiContentTypeCreateMapping>(
    contentType: K,
    documentId: string,
    data: Partial<StrapiContentTypeCreateMapping[K]>,
    params?: API.BaseQueryParams
  ): Promise<StrapiSingleContentTypeMapping[K]>
  
  // Generic fallback for update operations
  update<T extends API.Document = API.Document>(
    contentType: string,
    documentId: string,
    data: Partial<T>,
    params?: API.BaseQueryParams
  ): Promise<API.DocumentResponse<T>>

  // Delete operations (no data payload needed)
  delete(
    contentType: string,
    documentId: string,
    params?: API.BaseQueryParams
  ): Promise<void>

  single: {
    find(
      contentType: string, 
      params?: API.BaseQueryParams
    ): Promise<API.DocumentResponse>
    
    update(
      contentType: string,
      data: Record<string, unknown>,
      params?: API.BaseQueryParams
    ): Promise<API.DocumentResponse>
    
    delete(contentType: string, params?: API.BaseQueryParams): Promise<void>
  }

  files: {
    find(params?: FileQueryParams): Promise<FileListResponse>
    findOne(fileId: number): Promise<FileResponse>
    update(fileId: number, fileInfo: FileUpdateData): Promise<FileResponse>
    delete(fileId: number): Promise<FileResponse>
    upload(formData: FormData): Promise<FileResponse[]>
  }

  fetch(endpoint: string, options?: RequestInit): Promise<Response>
}

export const strapiService: StrapiService = {
  async find(contentType: string, params?: API.BaseQueryParams) {
    try {
      const client = getStrapiClient()
      const collection = client.collection(contentType)
      const response = await collection.find(params)
      return response
    } catch (error) {
      throw error
    }
  },

  async findOne(contentType: string, documentId: string, params?: API.BaseQueryParams) {
    try {
      const client = getStrapiClient()
      const collection = client.collection(contentType)
      const response = await collection.findOne(documentId, params)
      return response
    } catch (error) {
      throw error
    }
  },

  async create(
    contentType: string,
    data: Record<string, unknown>,
    params?: API.BaseQueryParams,
  ) {
    try {
      const client = getStrapiClient()
      const collection = client.collection(contentType)
      const response = await collection.create(data, params)
      return response
    } catch (error) {
      throw error
    }
  },

  async update(
    contentType: string,
    documentId: string,
    data: Record<string, unknown>,
    params?: API.BaseQueryParams,
  ) {
    try {
      const client = getStrapiClient()
      const collection = client.collection(contentType)
      const response = await collection.update(documentId, data, params)
      return response
    } catch (error) {
      throw error
    }
  },

  async delete(contentType: string, documentId: string, params?: API.BaseQueryParams) {
    try {
      const client = getStrapiClient()
      const collection = client.collection(contentType)
      const response = await collection.delete(documentId, params)
      return response
    } catch (error) {
      throw error
    }
  },

  single: {
    async find(contentType: string, params?: API.BaseQueryParams) {
      try {
        const client = getStrapiClient()
        const single = client.single(contentType)
        const response = await single.find(params)
        return response
      } catch (error) {
        throw error
      }
    },

    async update(
      contentType: string,
      data: Record<string, unknown>,
      params?: API.BaseQueryParams,
    ) {
      try {
        const client = getStrapiClient()
        const single = client.single(contentType)
        const response = await single.update(data, params)
        return response
      } catch (error) {
        throw error
      }
    },

    async delete(contentType: string, params?: API.BaseQueryParams) {
      try {
        const client = getStrapiClient()
        const single = client.single(contentType)
        const response = await single.delete(params)
        return response
      } catch (error) {
        throw error
      }
    },
  },

  files: {
    async find(params?: FileQueryParams) {
      try {
        const client = getStrapiClient()
        const response = await client.files.find(params)
        return response
      } catch (error) {
        throw error
      }
    },

    async findOne(fileId: number) {
      try {
        const client = getStrapiClient()
        const response = await client.files.findOne(fileId)
        return response
      } catch (error) {
        throw error
      }
    },

    async update(fileId: number, fileInfo: FileUpdateData) {
      try {
        const client = getStrapiClient()
        const response = await client.files.update(fileId, fileInfo)
        return response
      } catch (error) {
        throw error
      }
    },

    async delete(fileId: number) {
      try {
        const client = getStrapiClient()
        const response = await client.files.delete(fileId)
        return response
      } catch (error) {
        throw error
      }
    },

    async upload(formData: FormData) {
      try {
        const client = getStrapiClient()
        const response = await client.fetch('/upload', {
          method: 'POST',
          body: formData,
        })
        const jsonResponse = await response.json()
        return Array.isArray(jsonResponse) ? jsonResponse : [jsonResponse]
      } catch (error) {
        throw error
      }
    },
  },

  async fetch(endpoint: string, options?: RequestInit) {
    try {
      const client = getStrapiClient()
      const response = await client.fetch(endpoint, {
        ...options,
        headers: {
          ...options?.headers,
        },
      })
      return response
    } catch (error) {
      throw error
    }
  },

}

export { getStrapiClient as client }

export default strapiService
